import { STATUSPAGE } from '@repo/ui';
import {
  DeleteCompletedEvaluationUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllPersonCompletedEvaluationsUseCaseInterface,
  GetCompletedEvaluationDetailsUseCaseInterface,
} from 'domain/usecases';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';

export const usePersonCompletedEvaluationsTab = () => {
  const [completedEvaluations, setCompletedEvaluations] =
    useState<GetAllPersonCompletedEvaluationsUseCaseInterface.Result>([]);

  const [completedEvaluationDetails, setCompletedEvaluationDetails] =
    useState<GetCompletedEvaluationDetailsUseCaseInterface.Result>(null);

  const [groups, setGroups] = useState<GetAllGroupsUserUseCaseInterface.Result>(
    [],
  );
  const [loadingGroups, setLoadingGroups] = useState<boolean>(false);

  const [loadingCompletedEvaluations, setLoadingCompletedEvaluations] =
    useState<boolean>(false);

  const [requestStatus, setRequestStatus] = useState<STATUSPAGE>(
    STATUSPAGE.INITIAL,
  );

  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

  const [loadingDeleteEvaluation, setLoadingDeleteEvaluation] =
    useState<boolean>(false);

  const { groupsService, personService } = useService();

  const requestGetAllGroupsUser = useCallback(
    async (params: GetAllGroupsUserUseCaseInterface.Params) => {
      setLoadingGroups(true);

      try {
        const res = await groupsService.getAllGroupsUser(params);
        setGroups(res);
      } catch (err: unknown) {
        console.log(err);
      } finally {
        setLoadingGroups(false);
      }
    },
    [],
  );

  const requestGetAllPersonCompletedEvaluations = useCallback(
    async (params: GetAllPersonCompletedEvaluationsUseCaseInterface.Params) => {
      setLoadingCompletedEvaluations(true);
      setRequestStatus(STATUSPAGE.LOADING);

      try {
        const res =
          await personService.getAllPersonCompletedEvaluations(params);
        setCompletedEvaluations(res);
        setRequestStatus(STATUSPAGE.DONE);
      } catch (err) {
        setCompletedEvaluations([]);
        setRequestStatus(STATUSPAGE.ERROR);
        console.log(err);
        notifyError(err.message || 'Erro ao buscar avaliações realizadas');
      } finally {
        setLoadingCompletedEvaluations(false);
      }
    },
    [],
  );

  const requestGetCompletedEvaluationDetails = useCallback(
    async (params: GetCompletedEvaluationDetailsUseCaseInterface.Params) => {
      setLoadingDetails(true);

      try {
        const res = await personService.getCompletedEvaluationDetails(params);
        setCompletedEvaluationDetails(res);
      } catch (err: unknown) {
        console.log(err);
      } finally {
        setLoadingDetails(false);
      }
    },
    [],
  );

  const requestDeleteCompletedEvaluation = useCallback(
    async (
      params: DeleteCompletedEvaluationUseCaseInterface.Params,
      onSuccess: () => void,
    ) => {
      setLoadingDeleteEvaluation(true);

      try {
        const res = await personService.deleteCompletedEvaluation(params);
        onSuccess();
        notifySuccess(res.message || 'Avaliação excluída com sucesso');
      } catch (err: unknown) {
        console.log(err);
      } finally {
        setLoadingDeleteEvaluation(false);
      }
    },
    [],
  );

  return {
    groups,
    requestStatus,
    completedEvaluations,
    completedEvaluationDetails,
    loadingDetails,
    loadingGroups,
    loadingCompletedEvaluations,
    loadingDeleteEvaluation,
    requestGetAllGroupsUser,
    requestGetAllPersonCompletedEvaluations,
    requestGetCompletedEvaluationDetails,
    requestDeleteCompletedEvaluation,
  };
};
